import { useAtomValue } from 'jotai'
import { useLdFlag } from '@renderer/chat/hooks/useLdFlag'
import { RENDERER_OWNED_SUMMARIES_FLAG } from '@shared/audio-note-summary-ownership'
import { useIsSummaryPending } from './use-audio-note-selectors'
import { summaryGenerationTextAtom, summaryGenerationViewAtom, type SummaryGenerationView } from './summary-generation'

/** Whether this build starts and streams summaries in the renderer instead of main. */
export const useRendererOwnsSummaries = (): boolean => useLdFlag<boolean>(RENDERER_OWNED_SUMMARIES_FLAG, false) === true

/** Status of one note's renderer-owned generation; stable across text-only updates. */
export const useSummaryGenerationView = (noteId: string | null): SummaryGenerationView | null =>
  useAtomValue(summaryGenerationViewAtom(noteId ?? ''))

/** The streamed text of one note's renderer-owned generation. Subscribe only where it renders. */
export const useSummaryGenerationText = (noteId: string | null): string =>
  useAtomValue(summaryGenerationTextAtom(noteId ?? ''))

/**
 * Whether a summary is being generated for `noteId`, whichever process owns
 * it. Legacy main-owned state is a singleton, so there it answers for whatever
 * note main holds.
 */
export const useIsSummaryGenerating = (noteId: string | null): boolean => {
  const rendererOwned = useRendererOwnsSummaries()
  const view = useSummaryGenerationView(noteId)
  const legacyPending = useIsSummaryPending()
  if (!rendererOwned) {
    return legacyPending
  }
  return view?.status === 'preparing' || view?.status === 'streaming'
}
